/* eslint-disable @typescript-eslint/no-explicit-any */
import dayjs from 'dayjs';
import timeZoneMapping from "./timeZoneMapping.json";
import { getUserAttributes } from './auth';
import parse from "html-react-parser";
import sanitizeHtml from 'sanitize-html';
import {AccountThemeTagConfig, DateRangeValue, DEFAULT_FEATURE_MAP, FeatureMap, Options, SCORE_UI_TO_API_MAP} from "./types";
import { parseISO, isBefore, addMonths, format, addDays, startOfWeek, endOfWeek } from 'date-fns';
import { subMonths, subWeeks }  from "date-fns";
import React, { useEffect, useState } from 'react';
import CriticalFlag from "../assets/action-items/red-rated-flag-icon.svg";
import HighRiskFlag from "../assets/action-items/high-priority-flag.svg";
import MediumRiskFlag from "../assets/action-items/medium-priority-flag.svg";
import LowRiskFlag from "../assets/action-items/low-priority-flag.svg";
import { useSearchParams } from 'react-router-dom';
import redFlagIcon from "../assets/common/red-flag.svg";
import yellowFlagIcon from "../assets/common/yellow-flag.svg";
import greenFlagIcon from "../assets/common/green-flag.svg";
import greyFlagIcon from "../assets/common/grey-flag.svg";
import redFlagTrend from "../assets/accounts/red-flag-trend.svg";
import yellowFlagTrend from "../assets/accounts/yellow-flag-trend.svg";
import greenFlagTrend from "../assets/accounts/green-flag-trend.svg";
import greyFlagTrend from "../assets/accounts/grey-flag-trend.svg";

export const calculateDurationObject = (minutes: number) => {
  if (minutes >= 1440) {
    const days = Math.floor(minutes / 1440);
    const remainingMinutes = minutes % 1440;
    const hours = Math.floor(remainingMinutes / 60);
    const remainingMins = remainingMinutes % 60;
    return { days, hours, minutes: remainingMins };
  } else if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return { days: 0, hours, minutes: remainingMinutes };
  } else {
    return { days: 0, hours: 0, minutes };
  }
};

export const formatDurationString = (minutes: number): string => {
  if (minutes == 60) return `1 hr`;
  if (minutes < 60) return `${minutes} mins`;
  const formattedDuration = calculateDurationObject(minutes);
  const dayText = formattedDuration.days > 1 ? `${formattedDuration.days} days` : formattedDuration.days === 1 ? "1 day" : "";
  const hoursText = formattedDuration.hours > 0 ? `${formattedDuration.hours} hrs` : ""
  const minsText = formattedDuration.minutes > 0 ? `${formattedDuration.minutes} mins` : ""
  return `${dayText} ${hoursText} ${minsText}`;
};

export const formatHrsMinsDurationString = (minutes: number): string => {
  if (minutes == 60) return `1 h`;
  if (minutes < 60) return `${minutes} m`;

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  const hoursText = hours > 0 ? `${hours} ${hours === 1 ? "h" : 'hrs'}` : ""
  const minsText = remainingMinutes > 0 ? `${remainingMinutes} m` : ""
  return `${hoursText} ${minsText}`;
}

export const getDateRange = (timeRange: string) => {
  const currentDate = dayjs();
  let startDate, endDate;
  const utcTimeOffset = getUTCTimeZoneOffset();

  switch (timeRange) {
    case "next30Days":
      startDate = currentDate.add(1, 'day');
      endDate = currentDate.add(30, 'day');
      break;

    case "today":
      startDate = currentDate;
      endDate = currentDate;
      break;

    case "lastDay":
      startDate = currentDate.subtract(2, 'day');
      endDate = startDate;
      break;

    case "week":
      startDate = currentDate.subtract(7, 'day');
      endDate = currentDate.subtract(1, 'day');
      break;

    case "month":
      startDate = currentDate.subtract(30, 'day');
      endDate = currentDate.subtract(1, 'day');
      break;

    default:
      startDate = currentDate;
      endDate = currentDate;
      break;
  }

  const formattedStartDate = `${startDate.format("YYYY-MM-DD")}T00:00:00${utcTimeOffset}`;
  const formattedEndDate = `${endDate.format("YYYY-MM-DD")}T23:59:59${utcTimeOffset}`;

  return {
    start_time: formattedStartDate,
    end_time: formattedEndDate,
  };

};

export const gerPercentageDateRange = (startEndTime: { start_time: string; end_time: string }, timeRange: string) => {
  const currentDate = dayjs(startEndTime.start_time);
  const endDate = currentDate.subtract(1, 'day');
  let previousStartDate;
  const utcTimeOffset = getUTCTimeZoneOffset();

  switch (timeRange) {
    case "next30Days":
      previousStartDate = currentDate.subtract(30, 'day');
      break;

    case "today":
      previousStartDate = currentDate.subtract(1, 'day');
      break;

    case "lastDay":
      previousStartDate = currentDate.subtract(1, 'day');
      break;

    case "week":
      previousStartDate = currentDate.subtract(7, 'day');
      break;

    case "month":
      previousStartDate = currentDate.subtract(30, 'day');
      break;

    default:
      previousStartDate = currentDate.subtract(1, 'day');
      break;
  }
  const formattedPreviusStartDate = `${dayjs(previousStartDate).format("YYYY-MM-DD")}T00:00:00${utcTimeOffset}`;
  const formattedPreviusEndDate = `${endDate.format("YYYY-MM-DD")}T23:59:59${utcTimeOffset}`;

  return {
    previous_start_date: formattedPreviusStartDate,
    previous_end_date: formattedPreviusEndDate,
  };
}

export const calculatePercentageDifference = (currentValue: number, previousValue: number) => {
  if (previousValue === 0)
    return 0
  const percentageDifference =
    ((currentValue - previousValue) /
      previousValue) *
    100;
  const roundedPercentage = percentageDifference % 1 === 0 ? percentageDifference : percentageDifference.toFixed(2);
  return roundedPercentage;
}


export const getRefreshDataDateRange = () => {
  const utcTimeOffset = getUTCTimeZoneOffset();
  const startDate = `${dayjs().format("YYYY-MM-DD")}T00:00:00${utcTimeOffset}`;
  const endDate = `${dayjs().add(1, 'day').format("YYYY-MM-DD")}T23:59:59${utcTimeOffset}`;

  return {
    start_time: startDate,
    end_time: endDate,
  };
}

export const dateFromatter = (date: string, format = "MMM DD, YYYY") => {
  return dayjs(date).isValid() ? dayjs(date).format(format) : "";
};
export const validateDate = (value: any, format?: string) =>
  value && dayjs(value).isValid()
    ? dateFromatter(value, format)
    : "-";

export const getUTCTimeZoneOffset = () => {
  const utcOffset = dayjs().utcOffset();
  const offsetSign = utcOffset >= 0 ? '+' : '-';
  const offsetInHours = Math.floor(Math.abs(utcOffset) / 60);
  const offsetInMinutes = Math.abs(Math.abs(utcOffset) % 60);
  const formattedOffset = `${offsetSign}${offsetInHours
    .toString()
    .padStart(2, '0')}${offsetInMinutes.toString().padStart(2, '0')}`;
  return formattedOffset;
}

interface TimeZoneMap {
  [key: string]: string;
}

export const getTimeZoneAbbreviationByOffset = () => {
  const offset = getUTCTimeZoneOffset();
  const timeZoneMap: TimeZoneMap = timeZoneMapping;
  return timeZoneMap[offset] || 'Unknown';
}

export const getSyncedAt = (lastFetchedDate: string) => {
  const lastCalendarFetchedDate = dayjs(lastFetchedDate);
  const currentDate = dayjs();

  const diffInMinutes = currentDate.diff(lastCalendarFetchedDate, 'minutes');
  const diffInHours = currentDate.diff(lastCalendarFetchedDate, 'hours');
  const diffInDays = currentDate.diff(lastCalendarFetchedDate, 'days');
  const diffInWeeks = currentDate.diff(lastCalendarFetchedDate, 'weeks');
  const diffInMonths = currentDate.diff(lastCalendarFetchedDate, 'months');
  const diffInYears = currentDate.diff(lastCalendarFetchedDate, 'years');

  if (diffInMinutes < 60) {
    return `Synced ${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} before`;
  } else if (diffInHours < 24) {
    return `Synced ${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} before`;
  } else if (diffInDays < 7) {
    return `Synced ${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} before`;
  } else if (diffInWeeks < 4) {
    return `Synced ${diffInWeeks} ${diffInWeeks === 1 ? 'week' : 'weeks'} before`;
  } else if (diffInMonths < 12) {
    return `Synced ${diffInMonths} ${diffInMonths === 1 ? 'month' : 'months'} before`;
  } else {
    return `Synced ${diffInYears} ${diffInYears === 1 ? 'year' : 'years'} before`;
  }
}

export const getEventEndTime = (startTime: string, duration: number) => {
  if (startTime === "")
    return ""

  return dayjs(startTime)
    .add(duration, "minute")
    .toISOString();
}

export const getformattedDateString = (startTime: string, endTime: string) => {
  const startDate = dayjs(startTime);
  const endDate = dayjs(endTime);

  const formattedStartTime = startDate.format("ddd MMM D, hh:mm A");
  const formattedEndTime = endDate.format("hh:mm A");

  return `${formattedStartTime} - ${formattedEndTime}`;

}

export const EXCLUDED_DOMAINS = new Set([
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "yahoo.co.uk",
  "outlook.com",
  "live.com",
  "msn.com",
  "icloud.com",
  "me.com",
  "aol.com",
  "proton.me",
  "protonmail.com"
]);

export const isExcludedDomain = (domain?: string): boolean => {
  if (!domain) return false;
  return EXCLUDED_DOMAINS.has(domain.trim().toLowerCase());
};

export const getMeetingDomain = (meetingAttendeesDomains: string[]) => {
  const loggedInEmail = getUserAttributes()?.email;
  const regex = /^[^@]+@(.+)$/;
  const match = loggedInEmail?.match(regex);
  const loggedInUserDomain = match ? match[1] : null;

  // Calendar/demo meeting domain is gmail-only, not the share-dialog consumer denylist.
  const filteredDomains = meetingAttendeesDomains?.filter(item => {
    return item !== "gmail.com" && item !== loggedInUserDomain;
  });

  return filteredDomains?.length > 0 ? filteredDomains[0] : loggedInUserDomain;
};

export function capitalizeFirstLetter(text: string) {
  if (text) {
    const arrName = text.includes(".") ? text?.split(".") : text?.split(" ");
    return arrName
      ?.map((arr: string) => `${arr.charAt(0).toUpperCase()}${arr.slice(1)}`)
      .join(" ");
  }
  else return ""
}

export function extractNameFromEmail(name: string, email: string) {
  let nameString = "";
  if (name) {
    nameString = capitalizeFirstLetter(name)
  }
  else {
    const regex = /^([^@]+)@/;
    const match = email? email.match(regex) : null;
    nameString = match ? capitalizeFirstLetter(match?.[1] || '') : "";
  }
  return nameString;
}

export function getNameFromEmail(name: string, email: string) {
  if (name) {
    return capitalizeFirstLetter(name);
  } else {
    
    return capitalizeFirstLetter(email?.split('@')[0]);
  }
}

export function extractDomainFromEmail(email: string) {
  const regex = /@([^.]+)/;
  const match = email?.match(regex);
  return match ? capitalizeFirstLetter(match[1]) : null;
}

export function getWeekStrings(startDate: string, endDate: string) {
  const start = dayjs(startDate);
  const end = dayjs(endDate);

  const weekStrings = [];

  let weekStart = start.startOf('week');
  if (weekStart.month() !== start.month()) {
    weekStart = start.startOf('month');
  }
  let weekEnd = weekStart.endOf('week');

  while (weekEnd.isBefore(end)) {
    if (weekStart.isSame(weekEnd, 'day')) {
      weekStrings.push(weekStart.format('D-MMM'));
    } else {
      weekStrings.push(`${weekStart.format('D-MMM')} to ${weekEnd.format('D-MMM')}`);
    }
    weekStart = weekEnd.add(1, 'day').startOf('day');
    weekEnd = weekStart.add(6, 'day').endOf('day');
  }

  // Handle the last week
  if (weekStart.isBefore(end)) {
    weekStrings.push(`${weekStart.format('D-MMM')} to ${end.format('D-MMM')}`);
  }

  return weekStrings;
}


export function getDatesInRange(startDate: string, endDate: string) {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const dates = [];

  let currentDate = start;
  while (currentDate.isBefore(end, 'day')) {
    dates.push(currentDate.format('D MMM'));
    currentDate = currentDate.add(1, 'day');
  }

  // Check if the end date is included, if not, add it
  if (!dates.includes(end.format('D MMM'))) {
    dates.push(end.format('D MMM'));
  }

  return dates;
}


interface MeetingData {
  // location?: string;
  meetingLink?: string;
  description?: string;
  botStatusMessage?: string;
}


interface MeetingInfo {
  url: string;
  text: string;
}

export function extractMeetingUrl(data: MeetingData): MeetingInfo {
  // First: Use meetingLink if present
  const directUrl = data?.meetingLink;
  if (directUrl) {
    let text = "Join Meeting";
    if (/zoom\.us/.test(directUrl)) {
      text = "Join Zoom Meet";
    } else if (/meet\.google\.com/.test(directUrl)) {
      text = "Join Google Meet";
    } else if (/teams\.microsoft\.com/.test(directUrl)) {
      text = "Join Microsoft Teams";
    }
    return { url: directUrl, text };
  }

  const regex = /(?:<a\s+(?:[^>]*?\s+)?href="([^"]+)"|https?:\/\/\S+)(?=<\/a>|\s|$)/g;

  const description_urls = data?.description
    ? Array.from(data.description.matchAll(regex), match => match[1] || match[0].replace("</u>", ""))
    : [];

  const uniqueMatches = Array.from(new Set(description_urls));

  const [zoomUrls, otherUrls] = uniqueMatches.reduce(
    (acc, url) => {
      if (/zoom\.us/.test(url)) acc[0].push(url);
      else acc[1].push(url);
      return acc;
    },
    [[], []] as [string[], string[]]
  );

  const fallbackUrl = zoomUrls[0] || otherUrls[0] || "";
  const text = zoomUrls.length > 0
    ? "Join Zoom Meet"
    : /meet\.google\.com/.test(fallbackUrl)
      ? "Join Google Meet"
      : /teams\.microsoft\.com/.test(fallbackUrl)
        ? "Join Microsoft Teams"
        : "Join Meeting";

  return { url: fallbackUrl, text };
}


// todo:sanitnizeHtml does the same
// Define valid HTML tags
// const validTags = ['b', 'i', 'u', 'span', 'br', 'ul', 'ol', 'li', 'a'];

// function escapeInvalidTags(text: string) {
//   return text.replace(/<([^>]+)>/g, (match, p1) => {
//     // Check if it is a valid tag
//     const tag = p1.split(/\s+/)[0].replace(/\//, '');
//     if (validTags.includes(tag)) {
//       return match; // It's a valid tag, return as is
//     } else {
//       return match.replace(/</g, "&lt;").replace(/>/g, "&gt;"); // It's not a valid tag, escape it
//     }
//   });
// }

// function decodeHTMLEntities(text: string) {
//   return text.replace(/&lt;/g, "<").replace(/&gt;/g, ">");
// }

export function parseText(text: string) {
  if (text === undefined)
    return "";
  const sanitizedText = sanitizeHtml(text)
  // const escapedText: string = escapeInvalidTags(text);
  // const decodedText = decodeHTMLEntities(escapedText);
  const parsedContent = parse(sanitizeHtml(sanitizedText));
  return parsedContent;
}


export const generateLast12MonthsColumns = () => {
  return Array.from({ length: 12 }).map((_, index) => {
    const month = dayjs().subtract(index, 'month').format('MMM YYYY');
    return {
      field: month,               // Using month as the field
      header: month,          // Displaying the same value in the header
      width: 100,                 // Column width
      id: `column-${month}`       // Unique id for each column
    };
  }).reverse(); // Reverse to display from oldest to most recent
};


export const formatStartTime = (start_time: string): [string, string] => {
  let formattedDate = "";
  let localTime = "";

  if (start_time) {
    const dateObject = new Date(start_time);
    formattedDate = dateObject.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
    localTime = dateObject.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  return [formattedDate, localTime];
};

export const formatDate = (start_time: string): [string] => {
  let formattedDate = "";

  if (start_time) {
    const dateObject = new Date(start_time);
    formattedDate = dateObject.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  }

  return [formattedDate];
};
export const convertToUTC = (localTime: string): string => {
  return dayjs(localTime).utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
};

export function formatDateOnly(date: Date | string | null) {
  if (!date) return "";
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }
  return new Date(date).toLocaleDateString("en-CA");
}

export function clampDateToParentDueDate(
  date: Date | string | dayjs.Dayjs | null | undefined,
  parentDueDate?: string | null
) {
  if (!date || !parentDueDate) return date;
  const selected = dayjs(date);
  const max = dayjs(parentDueDate);
  if (!selected.isValid() || !max.isValid()) return date;
  const result = selected.isAfter(max, "day") ? max : selected;
  return result.format("YYYY-MM-DD");
}
export const getFormattedDurationFromRange = (startTime: string, endTime: string): string => {
  const start = dayjs(startTime);
  const end = dayjs(endTime);
  const totalMinutes = end.diff(start, 'minute');

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const hoursText = hours > 0 ? `${hours}h` : '';
  const minutesText = minutes > 0 ? `${minutes}m` : '';

  return `${hoursText}${hours && minutes ? ' ' : ''}${minutesText}`.trim();
};

export const formatDurationFromSeconds = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  let result = '';
  if (hours > 0) result += `${hours}h `;
  if (minutes > 0 || hours > 0) result += `${minutes}m `;
  result += `${remainingSeconds}s`;

  return result.trim();
};


type GraphDataPoint = {
  date: string;
  meetingCount: number;
  clientCount: number;
};

type Series = {
  label: string;
  data: number[];
  color: string;
  curve: 'monotoneX';
  showMark: boolean;
  area: boolean;
};

type GroupMode = 'day' | 'week' | 'month';

export const getChartSeries = (
  rawGraphData: GraphDataPoint[],
  groupMode: GroupMode,
  startDate: Date,
  endDate: Date,
  activeTab: 'meetings' | 'clients'
): { xLabels: string[]; primarySeries: Series; secondarySeries: Series } => {
  const xLabels: string[] = [];
  const parsedGraphData = rawGraphData.map(point => ({
    ...point,
    dateObj: parseISO(point.date),
  }));

  const meetingCountsMap = new Map<string, number>();
  const clientCountsMap = new Map<string, number>();

  if (groupMode === 'month') {
    let cursor = new Date(startDate);
    while (isBefore(cursor, endDate)) {
      const label = format(cursor, 'MMM');
      xLabels.push(label);
      meetingCountsMap.set(label, 0);
      clientCountsMap.set(label, 0);
      cursor = addMonths(cursor, 1);
    }

    parsedGraphData.forEach(({ dateObj, meetingCount, clientCount }) => {
      const label = format(dateObj, 'MMM');
      if (meetingCountsMap.has(label)) {
        meetingCountsMap.set(label, (meetingCountsMap.get(label) || 0) + meetingCount);
        clientCountsMap.set(label, (clientCountsMap.get(label) || 0) + clientCount);
      }
    });
  } else if (groupMode === 'week') {
    let cursor = new Date(startDate);
    const dayOfWeek = cursor.getDay();
    const daysUntilSaturday = (6 - dayOfWeek + 7) % 7;
    let firstWeekEnd = addDays(cursor, daysUntilSaturday);
    if (firstWeekEnd > endDate) firstWeekEnd = endDate;

    let label = `${format(cursor, 'MMM d')} - ${format(firstWeekEnd, 'MMM d')}`;
    xLabels.push(label);
    meetingCountsMap.set(label, 0);
    clientCountsMap.set(label, 0);
    cursor = addDays(firstWeekEnd, 1);

    while (isBefore(cursor, endDate)) {
      const weekStart = new Date(cursor);
      const weekEnd = addDays(weekStart, 6);
      const trimmedEnd = weekEnd > endDate ? endDate : weekEnd;
      label = `${format(weekStart, 'MMM d')} - ${format(trimmedEnd, 'MMM d')}`;
      xLabels.push(label);
      meetingCountsMap.set(label, 0);
      clientCountsMap.set(label, 0);
      cursor = addDays(cursor, 7);
    }

    parsedGraphData.forEach(({ dateObj, meetingCount, clientCount }) => {
      for (const label of xLabels) {
        const [startStr, endStr] = label.split(' - ');
        const start = new Date(`${startStr}, ${startDate.getFullYear()}`);
        const end = new Date(`${endStr}, ${startDate.getFullYear()}`);
        if (dateObj >= start && dateObj <= end) {
          meetingCountsMap.set(label, (meetingCountsMap.get(label) || 0) + meetingCount);
          clientCountsMap.set(label, (clientCountsMap.get(label) || 0) + clientCount);
          break;
        }
      }
    });
  } else {
    // groupMode === 'day'
    let cursor = new Date(startDate);
    while (cursor <= endDate) {
      const label = format(cursor, 'MMM d');
      xLabels.push(label);
      meetingCountsMap.set(label, 0);
      clientCountsMap.set(label, 0);
      cursor = addDays(cursor, 1);
    }

    parsedGraphData.forEach(({ dateObj, meetingCount, clientCount }) => {
      const label = format(dateObj, 'MMM d');
      if (meetingCountsMap.has(label)) {
        meetingCountsMap.set(label, (meetingCountsMap.get(label) || 0) + meetingCount);
        clientCountsMap.set(label, (clientCountsMap.get(label) || 0) + clientCount);
      }
    });
  }

  const meetingsData = xLabels.map(label => meetingCountsMap.get(label) || 0);
  const clientsData = xLabels.map(label => clientCountsMap.get(label) || 0);

  const primarySeries: Series = {
    label: activeTab === 'meetings' ? 'Interactions' : 'Clients',
    data: activeTab === 'meetings' ? meetingsData : clientsData,
    color: '#B902CA',
    curve: 'monotoneX',
    showMark: true,
    area: false,
  };

  const secondarySeries: Series = {
    label: activeTab === 'meetings' ? 'Clients' : 'Interactions',
    data: activeTab === 'meetings' ? clientsData : meetingsData,
    color: '#E09AE8',
    curve: 'monotoneX',
    showMark: true,
    area: false,
  };

  return { xLabels, primarySeries, secondarySeries };
};

export const generateTimeOptions = () => {
  const times: string[] = [];
  const start = 0; // 12:00 AM in minutes
  const end = 24 * 60;  // 12:00 PM in minutes

  for (let mins = start; mins < end; mins += 15) {
    const hours = Math.floor(mins / 60);
    const minutes = mins % 60;
    const displayHours = hours.toString().padStart(2, "0");
    const displayMinutes = minutes.toString().padStart(2, "0");
    times.push(`${displayHours}:${displayMinutes}`);
  }
  return times;
};

export const convert24To12 = (time: string): string => {
  const [hour, minute] = time.split(":").map(Number);
  const ampm = hour >= 12 ? "pm" : "am";
  const h = hour % 12 === 0 ? 12 : hour % 12;
  return `${h}:${minute.toString().padStart(2, "0")}${ampm}`;
};

export const convert12To24 = (time: string): string => {
  const [t, ampm] = [time.slice(0, -2), time.slice(-2)];
  const [hourPart, minutePart] = t.split(":").map(Number);
  let h = hourPart;
  const m = minutePart;
  if (ampm === "pm" && h !== 12) h += 12;
  if (ampm === "am" && h === 12) h = 0;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:00`;
};

export const formatClientDomain = (domain: string) => {
  if (!domain) return "";
  // remove top-level domain
  const name = domain.split(".")[0];
  // capitalize first letter
  return name.charAt(0).toUpperCase() + name.slice(1);
}

export const formatCurrency = (value: number | null | undefined): string => {
   const num = Number(value);
  if (!Number.isFinite(num)) return "$0";
  if (value == null || isNaN(value)) return "$0";
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${value}`;
};
type Preset = 'lastWeek' | 'lastMonth' | 'last3Months' | 'today' | 'last3MonthsFull' | 'last3MonthsFromYesterday';

interface DateRange {
  start_time: string;
  end_time: string;
}

export const getPresetDateRange = (preset: Preset): DateRange => {
  const now = new Date();
  let start: Date;
  let end: Date = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    23,
    59,
    59
  ); // end = today (end of day)

  switch (preset) {
    case "today":
      start = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        0,
        0,
        0
      );
      break;

    case "lastWeek":
      start = subWeeks(now, 1);
      break;

    case "lastMonth":
      start = subMonths(now, 1);
      break;

    case "last3Months":
      start = subMonths(now, 3);
      break;

    case "last3MonthsFull":
      start = new Date(
        now.getFullYear(),
        now.getMonth() - 3,
        1,
        0,
        0,
        0
      );
      end = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() - 1,
        23,
        59,
        59
      );
      break;

    case "last3MonthsFromYesterday": {
      const yesterday = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() - 1,
        23, 59, 59
      );

      start = new Date(
        yesterday.getFullYear(),
        yesterday.getMonth() - 3,
        yesterday.getDate(),
        0, 0, 0
      );

      end = yesterday;
      break;
    }

    default:
      start = new Date(now);
      break;
  }

  return {
    start_time: start.toISOString(),
    end_time: end.toISOString(),
  };
};

export const dateMonthFormater = (dateString: string): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

export const getPresetDateRangecalendar = (preset: "today" | "lastWeek" | "lastMonth" | "last3Months"): DateRange => {
  const now = new Date();
  let start: Date;
  let end: Date;

  switch (preset) {
    case "today":
      start = now;
      end = now;
      break;

    case "lastWeek":
      start = startOfWeek(subWeeks(now, 1), { weekStartsOn: 0 }); // Sunday
      end = endOfWeek(subWeeks(now, 1), { weekStartsOn: 0 }); // Saturday
      break;

    case "lastMonth":
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1); // first day of last month
      end = new Date(now.getFullYear(), now.getMonth(), 0); // last day of last month
      break;

    case "last3Months":
      start = new Date(now.getFullYear(), now.getMonth() - 3, 1); // first day 3 months ago
      end = now; // today
      break;

    default:
      start = now;
      end = now;
      break;
  }

  return {
    start_time: start.toISOString(),
    end_time: end.toISOString(),
  };
};

export const getBaseRoute = (pathname: string): string => {
  const parts = pathname.split("/").filter(Boolean);
  return parts.length >= 2 ? `/${parts[0]}/${parts[1]}` : `/${parts[0] ?? ""}`;
};

export const formatPresetLabel = (preset: string) => {
  if (!preset) return "";
  
  // Insert a space before each capital letter (camelCase → separate words)
  const spaced = preset.replace(/([a-z])([A-Z])/g, "$1 $2");
  
  // Capitalize first letter of each word
  return spaced
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" "); // <-- just a space
};

/** Convert seconds to HH:MM:SS */
export const secondsToHHMMSS = (seconds: number): string => {
  const h = Math.floor(seconds / 3600).toString().padStart(2, "0");
  const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, "0");
  const s = Math.floor(seconds % 60).toString().padStart(2, "0");
  return `${h}:${m}:${s}`;
};

export const formatClipDuration = (totalSec: number): string => {
  const diff = Math.max(0, totalSec);
  if (diff < 60) {
    return `${diff.toFixed(1)} ${diff === 1 ? "second" : "seconds"}`;
  }
  const mins = Math.floor(diff / 60);
  const secs = Math.floor(diff % 60);
  const minLabel = mins === 1 ? "min" : "mins";
  const secLabel = secs === 1 ? "second" : "seconds";
  return `${mins} ${minLabel} ${secs} ${secLabel}`;
};

export const formatClipTimeInput = (seconds: number): string => {
  const total = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = s.toString().padStart(2, "0");
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, "0")}:${ss}`;
  }
  return `${m}:${ss}`;
};

export const parseClipTimeInput = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parts = trimmed.split(":").map((part) => part.trim());
  if (parts.length === 1) {
    const seconds = Number(parts[0]);
    return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
  }
  if (parts.some((part) => part === "" || !/^\d+(\.\d+)?$/.test(part))) return null;
  if (parts.length === 2) {
    const minutes = Number(parts[0]);
    const seconds = Number(parts[1]);
    if (seconds >= 60) return null;
    return minutes * 60 + seconds;
  }
  if (parts.length === 3) {
    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);
    const seconds = Number(parts[2]);
    if (minutes >= 60 || seconds >= 60) return null;
    return hours * 3600 + minutes * 60 + seconds;
  }
  return null;
};

export const CLIP_SHARE_MAX_SEC = 180;
export const CLIP_DOWNLOAD_MAX_SEC = 300;
export const CLIP_DEFAULT_DURATION_SEC = 180;
export const CLIP_MAX_DURATION_MESSAGE = "Maximum clipping duration is 5 minutes.";

export const getClipActionState = (clipDuration: number) => {
  const duration = Math.max(0, clipDuration);
  const isOverDownloadMax = duration > CLIP_DOWNLOAD_MAX_SEC;
  return {
    duration,
    isOverDownloadMax,
    canShare: duration > 0 && duration <= CLIP_SHARE_MAX_SEC,
    canDownload: duration > 0 && duration <= CLIP_DOWNLOAD_MAX_SEC,
  };
};

export const buildVideoClipFilename = (meetingTitle: string) => {
  const safeName = String(meetingTitle || "Untitled").trim().replace(/\s+/g, "_") || "clip";
  return `${safeName}_${Date.now()}.mp4`;
};
export interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
}

export const useTimeLeft = (startTime: Date) => {
  const [timeLeft, setTimeLeft] = React.useState<TimeLeft>(() =>
    calculateTimeLeft(startTime)
  );

  React.useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft(startTime));
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [startTime]);

  return timeLeft;
};

/**
 * Utility function for time difference
 */
export function calculateTimeLeft(startTime: Date): TimeLeft {
  const currentTime = new Date();
  const timeDiff = startTime.getTime() - currentTime.getTime();

  const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

  return {
    days: Math.max(days, 0),
    hours: Math.max(hours, 0),
    minutes: Math.max(minutes, 0),
  };
}

/**
 * Utility function to map sentiment to color
 */
export function getSentimentColor(sentiment: string | null): string | null {
  switch (sentiment) {
    case "Positive":
      return "#04BD00"; // Green
    case "Negative":
      return "#F4442F"; // Red
    case "Neutral":
      return "#FFCD2A"; // Orange
    default:
      return null;
  }
}

export type MeetingSentimentDisplay =
  | { type: "upcoming" }
  | { type: "inProgress" }
  | { type: "rating"; color: string; sentiment: string }
  | { type: "botStatus"; message: string }
  | { type: "recordingPresent" }
  | { type: "noRecording" };

export type MeetingSentimentInput = {
  startTime: Date | string;
  duration?: number | null;
  endTime?: Date | string | null;
  sentiment?: string | null;
  isPreviousAsset?: boolean;
  isPresentAsset?: boolean;
  currentTime?: Date;
  botStatus?: string | null;
  botStatusMessage?: string | null;
  noSentimentInRatingColumnEnabled?: boolean;
};

const HIDDEN_RATING_BOT_STATUSES = new Set(["scheduled", "not scheduled"]);

export const isHiddenRatingBotStatus = (status?: unknown): boolean => {
  if (typeof status !== "string") return false;
  return HIDDEN_RATING_BOT_STATUSES.has(status.trim().toLowerCase());
};

function getRatingOrBotStatusDisplay(
  color: string,
  sentiment: string,
  rawStatus?: unknown,
  noSentimentInRatingColumnEnabled?: boolean
): MeetingSentimentDisplay {
  if (noSentimentInRatingColumnEnabled) {
    const status = typeof rawStatus === "string" ? rawStatus.trim() : "";
    if (status && !isHiddenRatingBotStatus(status)) {
      return { type: "botStatus", message: status };
    }
    return { type: "recordingPresent" };
  }

  return { type: "rating", color, sentiment };
}

export function getMeetingSentimentDisplay({
  startTime,
  duration,
  endTime,
  sentiment,
  isPreviousAsset,
  isPresentAsset,
  currentTime = new Date(),
  botStatus,
  noSentimentInRatingColumnEnabled,
}: MeetingSentimentInput): MeetingSentimentDisplay {
  const startTimeDate =
    startTime instanceof Date ? startTime : new Date(startTime);
  const startTimeMs = startTimeDate.getTime();

  let endTimeDate: Date | null = null;
  if (endTime) {
    const parsed = endTime instanceof Date ? endTime : new Date(endTime);
    if (!isNaN(parsed.getTime())) {
      endTimeDate = parsed;
    }
  }
  if (!endTimeDate && duration !== undefined) {
    const safeDuration = duration === null ? 0 : Number(duration);
    if (Number.isFinite(safeDuration)) {
      endTimeDate = new Date(startTimeMs + safeDuration * 60000);
    }
  }

  const color = getSentimentColor(sentiment ?? null);
  const isUpcoming = startTimeDate > currentTime;
  const isInProgress = endTimeDate
    ? currentTime >= startTimeDate && currentTime <= endTimeDate
    : false;
  const isCompleted = endTimeDate ? currentTime >= endTimeDate : false;

  const rawStatus =
    (typeof botStatus === "string" && botStatus.trim()) ||
    "";
  const status = rawStatus?.trim();

  if (isUpcoming) {
    return { type: "upcoming" };
  }

  // Live meeting:
  // If in progress and there is no present asset (i.e. no asset, or only previous asset for a recurring series),
  // it must stay in progress until the meeting ends.
  if (isInProgress && !isPresentAsset) {
    return { type: "inProgress" };
  }

  const hasAsset = Boolean(isPreviousAsset || isPresentAsset);

  if (!hasAsset) {
    if (
      isCompleted &&
      status &&
      !isHiddenRatingBotStatus(status)
    ) {
      return { type: "botStatus", message: status };
    }
    return { type: "noRecording" };
  }

  // Has asset (isPreviousAsset || isPresentAsset)
  if (!isInProgress && !isCompleted) {
    return { type: "noRecording" };
  }

  // If previous-only asset, it can only show rating or recordingPresent once completed (in-progress was handled above).
  if (isPreviousAsset && !isPresentAsset && !isCompleted) {
    return { type: "noRecording" };
  }

  if (color) {
    return getRatingOrBotStatusDisplay(
      color,
      sentiment as string,
      rawStatus,
      noSentimentInRatingColumnEnabled
    );
  }

  // Has asset, but no sentiment rating (e.g. sentiment is null / empty)
  if (
    noSentimentInRatingColumnEnabled &&
    status &&
    !isHiddenRatingBotStatus(status)
  ) {
    return { type: "botStatus", message: status };
  }

  return { type: "recordingPresent" };
}

export const getUniqueAssignees = (dropdownUserData: any) => {
  const list = dropdownUserData?.usersList || [];
  const usersMap = new Map();
  list.forEach((user: any) => {
    const userId = user.profileOrgRelationshipId || user.name;
    if (userId && !usersMap.has(userId)) {
      usersMap.set(userId, {
        label: user.name || user.email || userId,
        value: userId,
        avatar: user?.pictureUrl,
        email: user?.email,
      });
    }
  });
  return Array.from(usersMap.values());
};

export const getUniqueAccounts = (dropdownUserData: any) => {
  const list = dropdownUserData?.accountList || [];
  const accountsMap = new Map();

  list.forEach((account: any) => {
    const clientOrgId = account?.clientOrgId;
    const name = account?.clientName;

    // Ensure both domain and name exist
    if (clientOrgId && !accountsMap.has(clientOrgId)) {
      accountsMap.set(clientOrgId, {
        label: capitalizeFirstLetter(name) || clientOrgId, // label shown in dropdown
        value: clientOrgId, // actual filter value
      });
    }
  });
  return Array.from(accountsMap.values());
};

export const PRIORITY_OPTIONS = [
  { label: "Critical", value: "critical", icon: CriticalFlag },
  { label: "High", value: "high", icon: HighRiskFlag },
  { label: "Medium", value: "medium", icon: MediumRiskFlag },
  { label: "Low", value: "low", icon: LowRiskFlag },
];

export const getLoggedInUserDomain = () => {
  const email = getUserAttributes()?.email;
  return email ? email.split("@")[1] : undefined;
};

export const getStatusOptions = () => {
  const domain = getLoggedInUserDomain();

  if (domain === "anarix.io") {
    return [
      { label: "Open", value: "open", color: "#F5A524" },
      { label: "In Progress", value: "in_progress", color: "#72BDE7" },
      { label: "DevBox", value: "devbox", color: "#298EC7" },
      { label: "Parent Branch", value: "parent_branch", color: "#67450F" },
      { label: "PR Review", value: "pr_review", color: "#AE751A" },
      { label: "Dev Testing", value: "dev_testing", color: "#B6A3EC" },
      { label: "Dev Approved", value: "dev_approved", color: "#6036D6" },
      { label: "Test Approved", value: "test_approved", color: "#98E6BB" },
      { label: "Prod Testing", value: "prod_testing", color: "#A6A6B2" },
      { label: "Closed", value: "closed", color: "#1BB761" },
    ];
  }

  if (domain === "7thgear.ai") {
    return [
      { label: "To Do", value: "to_do", color: "#ff9800" },
      { label: "In Progress", value: "in_progress", color: "#1976d2" },
      { label: "PR Review", value: "pr_review", color: "#875B14" },
      { label: "Product Testing", value: "product_testing", color: "#195678" },
      { label: "Done", value: "done", color: "#158F4C" },
      { label: "Won't Do", value: "wont_do", color: "#B53737" },
    ];
  }

  return [
    { label: "To Do", value: "to_do", color: "#ff9800" },
    { label: "In Progress", value: "in_progress", color: "#1976d2" },
    { label: "Done", value: "done", color: "#158F4C" },
    { label: "Won't Do", value: "wont_do", color: "#B53737" },
  ];
};

export function useDebounce<T>(value: T, delay: number = 1000): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (typeof value === "string") {
        setDebouncedValue(value.trim() as T);
      } else {
        setDebouncedValue(value);
      }
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// utils/avatar.ts
export const COLORS = [
  "#F44336", "#E91E63", "#9C27B0", "#673AB7",
  "#3F51B5", "#2196F3", "#03A9F4", "#00BCD4",
  "#009688", "#4CAF50", "#8BC34A", "#CDDC39",
  "#FFC107", "#FF9800", "#FF5722", "#795548",
];

// Always derive initials from name if available, otherwise email
export function getAvatarInitials(name?: string | null, email?: string | null): string {
  const source = name?.trim() || email?.trim();
  if (!source) return "?";

  if (source.includes(" ")) {
    const words = source.split(" ").filter(Boolean);
    return words.slice(0, 1).map((w) => w[0].toUpperCase()).join("");
  }

  if (source.includes("@")) {
    const localPart = source.split("@")[0];
    const parts = localPart.split(/[.\-_]/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return localPart.slice(0, 1).toUpperCase();
  }

  return source.slice(0, 1).toUpperCase();
}

// Generate consistent color based on email if available, else name
export function getAvatarColor(name?: string | null, email?: string | null): string {
  const base = email?.trim()?.toLowerCase() || name?.trim()?.toLowerCase() || "?";
  let hash = 0;
  for (let i = 0; i < base.length; i++) {
    hash = base.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % COLORS.length;
  return COLORS[index];
}
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

export const getUniqueTags = (dropdownUserData: any) => {
  const list = dropdownUserData?.tagsList || [];
  const tagsMap = new Map();

  list.forEach((tags: any) => {
    const tagId = tags?.tagId;
    const name = tags?.tagName;

    if (tagId && !tagsMap.has(tagId)) {
      tagsMap.set(tagId, {
        label: name,
        value: tagId,
        type: "tags",
      });
    }
  });
  return Array.from(tagsMap.values());
};

export const highRadiusSpecialUserEmails = [
  "srinivasg.7th@highradius.com",
];

export const isHighRaduisSpecialUser = (): boolean => {
  const loggedInEmail = getUserAttributes()?.email || "";
  return highRadiusSpecialUserEmails.includes(loggedInEmail);
};

export const extractFirstname = (name: string) => {
  if (!name) return "";
  const clean = name.trim().replace(/\s+/g, " "); // normalize spaces
  return clean.split(" ")[0];
};

export const getTimeAgo = (timestamp: string | Date): string => {
  if (!timestamp) return "";

  const now = new Date();
  const time = typeof timestamp === "string" ? new Date(timestamp) : timestamp;

  const diffMs = now.getTime() - time.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSec < 30) return "Just now";
  if (diffMin < 1) return `${diffSec} sec${diffSec > 1 ? "s" : ""} ago`;
  if (diffMin < 60) return `${diffMin} min${diffMin > 1 ? "s" : ""} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;

  return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
}

export const getCustomDefaultRange = (): DateRangeValue => {
  const today = new Date();

  // First day of previous month
  const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  start.setHours(0, 0, 0, 0); // normalize start-of-day LOCAL

  // Last day of 2 months ahead
  const end = new Date(today.getFullYear(), today.getMonth() + 3, 0);
  end.setHours(23, 59, 59, 999); // normalize end-of-day LOCAL

  return {
    preset: "custom",
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
};

export const getSubscriberOptions = (
  dropdownUserData?: {
    usersList: any[];
    spacesList: any[];
  }
): Options[] => {
  if (!dropdownUserData) return [];

  const userOptions: Options[] =
    dropdownUserData.usersList?.map((user) => ({
      label: user.name,
      value: user.profileOrgRelationshipId,
      avatar: user.pictureUrl || user.avatar_url || "",
      email: user.email,
      type: "assignee",          // UI type
      entityType: "user",        // ✅ DATA type
    })) || [];

  const spaceOptions: Options[] =
    dropdownUserData.spacesList?.map((space) => ({
      label: space.spaceName,
      value: space.spaceId,
      type: "subscribers",       // reuse UI bucket
      entityType: "space",       // ✅ DATA type
    })) || [];

  return [...userOptions, ...spaceOptions];
};


export const isDateRange = (
    value: unknown
    ): value is { startDate: string; endDate: string } => {
    return (
        typeof value === "object" &&
        value !== null &&
        "startDate" in value &&
        "endDate" in value
    );
};

export const DUE_DATE_FILTER_OPTIONS = [
  {
    label: "Overdue",
    value: "overdue",
  },
];


interface NormalizeArgs {
  columnKey: string;
  value: any;
  nextFilters: any;
}

export const normalizeOverdueRange = (
  startDate?: string,
  endDate?: string,
  isOverdue?: boolean
) => {
  if (!isOverdue) {
    return { startDate, endDate };
  }

  // 🔑 Always force last 2 years → yesterday for overdue
  const start = dayjs()
    .subtract(2, "year")
    .startOf("day")
    .toISOString();

  const end = dayjs()
    .subtract(1, "day")
    .endOf("day")
    .toISOString();

  return {
    startDate: start,
    endDate: end,
  };
};

export const normalizeColumnFilters = ({
  columnKey,
  value,
  nextFilters,
}: NormalizeArgs) => {
  const statusList = Array.isArray(nextFilters.status)
    ? [...nextFilters.status]
    : [];

  const dueDateList = Array.isArray(nextFilters.dueDate)
    ? [...nextFilters.dueDate]
    : [];

  if (
    columnKey === "dueDate" &&
    Array.isArray(value) &&
    value.includes("overdue") &&
    statusList.includes("done")
  ) {
    nextFilters.status = statusList.filter(s => s !== "done");
  }

  if (
    columnKey === "status" &&
    statusList.includes("done") &&
    dueDateList.includes("overdue")
  ) {
    nextFilters.dueDate = dueDateList.filter(d => d !== "overdue");
  }

  return nextFilters;
}

export const GROUP_BY_MAP: Record<string, string> = {
  s: "status",
  i: "interaction",
  r: "recurring_interaction",
  c: "client",
  a: "assignee",
};

export const PRESET_MAP: Record<string, string> = {
  t: "today",
  c: "custom",
};

export const STATUS_MAP: Record<string, string> = {
  t: "to_do",
  i: "in_progress",
  pr: "pr_review",
  pt: "product_testing",
  d: "done",
};

export interface BackendDateRange {
  preset: string;         
  startDate?: string;      
  endDate?: string; 
}

export const buildDateRange = (
  backendRange: BackendDateRange
): DateRangeValue => {
  const { preset, startDate, endDate } = backendRange;

  return {
    preset,
    startDate: startDate
      ? dayjs(startDate, "YYYYMMDD").startOf("day").toISOString()
      : "",
    endDate: endDate
      ? dayjs(endDate, "YYYYMMDD").endOf("day").toISOString()
      : "",
  };
};

export const OVERDUE_MAP: Record<string, boolean> = {
  t: true,
  f: false,
};


export const isValidDate = (date: any): boolean => {
  if (!date) return false;
  const parsed = typeof date === "string" ? new Date(date) : date;
  return parsed instanceof Date && !isNaN(parsed.getTime());
};

export enum ActionItemGroupByKey {
  PRIORITY = "PRIORITY",
  RESOURCES = "RESOURCES",
  CLIENT = "CLIENT",
  DELAY = "DELAY",
  ARR = "ARR",
  ORGANIZER = "ORGANIZER",
}

export const ACTION_ITEM_GROUP_BY_OPTIONS = [
  { label: "Priority", value: ActionItemGroupByKey.PRIORITY },
  { label: "Resources", value: ActionItemGroupByKey.RESOURCES },
  { label: "Account", value: ActionItemGroupByKey.CLIENT },
  { label: "Delay", value: ActionItemGroupByKey.DELAY },
] as const;

export const PRIORITY_BAR_CONFIG = [
  { label: "Low", value: "low" },
  { label: "Medium", value: "medium" },
  { label: "High", value: "high" },
  { label: "Critical", value: "critical" },
  { label: "No Priority", value: "no_priority"},
];


export const DELAY_CATEGORY_CONFIG = [
  { key: "ON_TIME", label: "On Time", },
  { key: "DELAY_LT_7", label: "Delayed < 7 days" },
  { key: "DELAY_7_30", label: "Delayed 7–30 days", },
  { key: "DELAY_GT_30", label: "Delayed 30+ days" },
] as const;

export const PRIORITY_OPTION = [
  { label: "Critical", value: "critical", icon: CriticalFlag },
  { label: "High", value: "high", icon: HighRiskFlag },
  { label: "Medium", value: "medium", icon: MediumRiskFlag },
  { label: "Low", value: "low", icon: LowRiskFlag },
];

export const getDisplayName = (fullName?: string, email?: string) => {
  if (!fullName) return email ?? "";

  const parts = fullName.trim().split(/\s+/);

  // If only one word, return it
  if (parts.length === 1) return parts[0];

  // Prefer first name, fallback to last name
  return parts[0] || parts[parts.length - 1];
};
export const getParamFromUrl = () => {
  const [searchParams] = useSearchParams();
    const ctx = searchParams.get("ctx");
    if (!ctx) return null;

    try {
      const decoded = JSON.parse(atob(ctx));

      return {
        groupBy: decoded.g ? GROUP_BY_MAP[decoded.g] : undefined,

        status: Array.isArray(decoded.s)
          ? decoded.s.map((s: string) => STATUS_MAP[s]).filter(Boolean)
          : undefined,

        dueDate: decoded.d
          ? {
              preset: PRESET_MAP[decoded.d.p],
              startDate: decoded.d.s,
              endDate: decoded.d.e,
            }
          : undefined,
          overdue: decoded.od
          ? OVERDUE_MAP[decoded.od]
          : undefined,
      };
    } catch {
      return null;
    }
  };

  export const getLastTwoYearsToYesterdayRange = () => {
    const start = dayjs()
      .subtract(2, "year")
      .startOf("day");
  
    const end = dayjs()
      .subtract(1, "day")
      .endOf("day");
  
    return {
      start_time: start.toISOString(),
      end_time: end.toISOString(),
    };
  };  

  export const SUPPORT_TASK_DEFAULTS: Record<
  string,
  {
    assignee: string;
    subscribers: string[];
    tagId: string;
    dueInDays: number;
  }
> = {
  "7thgear": {
    assignee: "bcc6275c-75f8-42e5-b3e8-058e40eefce1",
    subscribers: [
      "c364971e-106f-4d2d-8525-44c920307f5b",
      "2ddf13a8-ae62-4bcb-bdd9-f08ac7983301",
    ],
    tagId: "d097a791-f438-4215-a281-3fb1cd5c91f4",
    dueInDays: 14,
  },
  "highradius": {
    assignee: "e4b786bf-9230-4aea-b3f7-6eaec9f6741a",
    subscribers: [
      "dce6dcfc-3587-4ce5-b9df-39d28d79d7c5",
      "0d45fca4-faa8-4efb-b27e-5d076ddf7658",
    ],
    tagId: "6e498e0a-0875-4444-92b2-87ee49eebe09",
    dueInDays: 14,
  },
};

export const normalizeFeatureFlags = (apiData: any[]): FeatureMap => {
  const map: FeatureMap = { ...DEFAULT_FEATURE_MAP };

  apiData.forEach(item => {
    if (!item.featureName) return;

    if (!(item.featureName in map)) {
      console.warn("⚠️ Unknown backend feature:", item.featureName);
      return;
    }

    map[item.featureName] = Boolean(item.isActive && item.isAllowedForUser);
  });

  return map;
};

export const SCORE_API_TO_UI_MAP: Record<string, string> = Object.entries(
  SCORE_UI_TO_API_MAP
).reduce((acc, [uiKey, apiVal]) => {
  if (apiVal) acc[apiVal] = uiKey;
  return acc;
}, {} as Record<string, string>);

export const getScoreSentimentFromFraction = (
  score?: string
): "positive" | "negative" | "not_rated" => {
  if (!score || !score.includes("/")) return "not_rated";

  const [numStr, denStr] = score.split("/");

  const num = Number(numStr);
  const den = Number(denStr);

  if (isNaN(num) || isNaN(den) || den === 0) return "not_rated";

  return num === den ? "positive" : "negative";
};
export interface NumberFilterValue {
  operator: "gt" | "lt";
  value: number;
}

export type TimelineItem = {
  id: string;
  start_time: string;
  type: "interaction" | "email";
  title: string;
  overallSentiment?: string | undefined;
  from?: string;
  to?: string;
  summary?: string;
  subject?: string;
};

export type GroupedTimelineItem = {
  dateKey: string; // YYYY-MM-DD
  date: string;
  items: TimelineItem[];
};

export type TimelineMonthEndMarker = {
  monthKey: string;
  icon: string;
  position: number;
  align: "left" | "center" | "right";
};

export type TimelineTooltipPayload = {
  mode: "single" | "group" | "mixed";
  interactions: TimelineItem[];
  emailIds?: string[];
  navigationState?: Record<string, any>;
  groupDateKey?: string;
  anchorInteractionId?: string;
};

export const groupTimelineItemsByDate = (
  items: TimelineItem[],
  separateByType: boolean = false
) => {
  const grouped = new Map<string, TimelineItem[]>();

  items.forEach((item) => {
    const date = new Date(item.start_time).toISOString().split("T")[0];
    const dateKey = separateByType ? `${date}-${item.type}` : date;
    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, []);
    }

    grouped.get(dateKey)?.push(item);
  });

  return Array.from(grouped.entries()).map(
    ([dateKey, groupedItems]) => ({
      dateKey,
      items: groupedItems.sort(
        (a, b) =>
          new Date(a.start_time).getDate() -
          new Date(b.start_time).getDate()
      ),
    })
  );
};

export const mapToTimelineItems = (
  interactions: {
    interactionId: string;
    title?: string;
    startTime: string;
    overallSentiment?: string;
  }[] = [],
  emails:
    | Record<string, string>
    | {
        id?: string;
        messageDate?: string;
        startTime?: string;
        subject?: string;
        body?: string;
        fromEmail?: string;
        toEmail?: string[];
      }[] = {}
): TimelineItem[] => {
  const interactionItems: TimelineItem[] = interactions.map((interaction) => ({
    id: interaction.interactionId,
    start_time: interaction.startTime,
    type: "interaction",
    title: interaction.title || "",
    overallSentiment: interaction.overallSentiment,
  }));

  const emailItems: TimelineItem[] = Array.isArray(emails)
    ? emails.map((email) => ({
        id: email.id || "",
        start_time: email.messageDate || email.startTime || "",
        type: "email",
        title: email.subject || "Email",
      }))
    : Object.entries(emails).map(([id, date]) => ({
        id,
        start_time: date,
        type: "email",
        title: "Email",
      }));

  return [...interactionItems, ...emailItems]
    .filter((item) => item.start_time && !Number.isNaN(new Date(item.start_time).getTime()))
    .sort(
      (a, b) =>
        new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
    );
};
export const getSentimentMeta = (score?: string) => {
  if (!score) return null;

  const normalized = getScoreSentimentFromFraction(score);

  if (normalized === "positive") {
    return { label: "Positive", color: "#1BB761" };
  }

  if (normalized === "negative") {
    return { label: "Negative", color: "#E84646" };
  }

  return null;
};


export const isNewItem = (createdAt: string, hours = 24) => {
  const createdTime = new Date(createdAt).getTime(); // UTC-safe
  return Date.now() - createdTime < hours * 60 * 60 * 1000;
};

export const normalizeToFullDay = (start: Date, end: Date) => {
  const getTodayRange = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const todayEnd = new Date(today);
    todayEnd.setHours(23, 59, 59, 999);

    return { startDate: today, endDate: todayEnd };
  };
  if (!start || !end) return getTodayRange();
  const startDate = new Date(start);
  const endDate = new Date(end);
  // Handle invalid dates
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return getTodayRange();
  }
  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(23, 59, 59, 999);
  return { startDate, endDate };
};

export const getTrailingWeeksUTC = (
  count: number = 5,
  weekStartsOn: 0 | 1 = 0 // 0 = Sunday, 1 = Monday
) => {
  const weeks: Record<string, string> = {};
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now);

    // go back i weeks
    d.setDate(d.getDate() - (i + 1) * 7);

    // find start of week in LOCAL time
    const day = d.getDay();
    const diff = (day - weekStartsOn + 7) % 7;
    d.setDate(d.getDate() - diff);

    // local midnight
    d.setHours(0, 0, 0, 0);

    // convert to UTC ISO
    weeks[`week${count - i}`] = d.toISOString();
  }

  return weeks;
};
export function normalizeLeadingCapital(text?: string): string {
  if (!text) return "";

  return text.replace(/^[a-z]/, (char) => char.toUpperCase());
}

export const dateFormatter = (date: any) => {
  const d = new Date(date);

  const time = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();

  return `${time}, ${day}/${month}/${year}`;
};
export const getFileColor = (ext: string) => {
  switch (ext) {
    case "pdf": return "#F14848";
    case "png":
    case "jpg":
    case "jpeg": return "#4881F1";
    case "mp4":
    case "m4a": return "#6D56F8";
    case "xls":
    case "xlsx":
    case "txt":
    case "csv": return "#0F8105";
    default: return "#9E9E9E";
  }
}

export const getActiveAccounts = (dropdownUserData: any) => {
  const list = dropdownUserData?.accountList || [];
  const accountsMap = new Map();

  list.forEach((account: any) => {
    const accountId = account?.accountId;
    const name = account?.accountName;

    // Ensure both accountId and name exist
    if (accountId && !accountsMap.has(accountId)) {
      accountsMap.set(accountId, {
        label: capitalizeFirstLetter(name) || accountId, // label shown in dropdown
        value: accountId, // actual filter value
      });accountId
    }
  });
  return Array.from(accountsMap.values());
};

export const getDaysLeftNumber = (expiry: string) => {
  if (!expiry) return null;

  const now = new Date();
  const expiryDate = new Date(expiry);

  const diffTime = expiryDate.getTime() - now.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

export const RELATIONSHIP_TYPE_OPTIONS = [
  "CLIENT",
  "VENDOR",
  "INVESTOR",
  "PROSPECT",
  "PARTNER",
] as const;

export const getTrendIcon = (value: unknown) => {
  const v = String(value);
  switch (v) {
    case "1":
      return redFlagIcon;
    case "5":
      return yellowFlagIcon;
    case "9":
      return greenFlagIcon;
    default:
      return greyFlagIcon;
  }
};

export const getMonthlyTrendIcon = (value: unknown) => {
  const v = String(value);
  switch (v) {
    case "1":
      return redFlagTrend;
    case "5":
      return yellowFlagTrend;
    case "9":
      return greenFlagTrend;
    default:
      return greyFlagTrend;
  }
};
export const getMonthKey = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  return `${year}-${month}`;
};

export const getTimelinePositionPercent = (
  date: Date,
  start: Date,
  end: Date
): number => {
  const total = end.getTime() - start.getTime();
  if (total <= 0 || Number.isNaN(date.getTime())) return 0;

  const current = date.getTime() - start.getTime();
  return Math.min(Math.max((current / total) * 100, 0), 100);
};

export const buildTimelineMonthEndMarkers = (
  monthlyScore: Record<string, string> | null | undefined,
  startDate: Date,
  endDate: Date
): TimelineMonthEndMarker[] => {
  const markers: TimelineMonthEndMarker[] = [];

  if (!startDate || !endDate) return markers;

  const normalizedStartDate = new Date(startDate);
  normalizedStartDate.setHours(0, 0, 0, 0);

  const normalizedEndDate = new Date(endDate);
  normalizedEndDate.setHours(23, 59, 59, 999);

  const current = new Date(
    normalizedStartDate.getFullYear(),
    normalizedStartDate.getMonth(),
    1
  );

  while (current <= normalizedEndDate) {
    const year = current.getFullYear();
    const month = current.getMonth() + 1;

    const monthKey = `${year}-${String(month).padStart(2, "0")}`;

    //  Get score if exists
    const score = monthlyScore?.[monthKey];

    //  Get icon OR fallback
    let icon = getMonthlyTrendIcon(score);

    // Month end date
    const monthEndDate = new Date(year, month, 0, 23, 59, 59, 999);
    if (
      monthEndDate >= normalizedStartDate &&
      monthEndDate <= normalizedEndDate
    ) {
      const position = getTimelinePositionPercent(
        monthEndDate,
        normalizedStartDate,
        normalizedEndDate
      );

      markers.push({
        monthKey,
        icon,
        position,
        align:
          position <= 2 ? "left" : position >= 98 ? "right" : "center",
      });
    }

    // move to next month
    current.setMonth(current.getMonth() + 1);
  }

  return markers;
};

export const accountRatingOptions = [
  { label: "Promoter", value: "Promoter", labelColour: "#32BD00" },
  { label: "Neutral", value: "Neutral", labelColour: "#FFCD2A" },
  { label: "Detractor", value: "Detractor", labelColour: "#F4442F" },
  { label: "Not Rated", value: "NotRated", labelColour: "#EFEBFB" },
];

export const relationshipTypeOptions = [
  { label: "Client", value: "client" },
  { label: "Vendor", value: "vendor" },
  { label: "Investor", value: "investor" },
  { label: "Prospect", value: "prospect" },
  { label: "Partner", value: "partner" },
] as const;

export const accountStatusOptions = [
  { label: "Active", value: "active" },
  { label: "In-Active", value: "inactive" }
];

export const flightRiskOptions = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];

export const getNormalizedTimeZone = () => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (tz === "Asia/Calcutta") return "Asia/Kolkata";

  return tz;
};

export const isCondensedTranscript = (value?: string) => {
  const normalized = value?.toLowerCase() || "";

  return (
    normalized.includes("condensed_transcript") ||
    normalized.includes("condense_transcript")
  );
};

export const interactionTypeOptions = [
  { label: "CV Call", value: "CV_CALL" },
  { label: "Sales", value: "SALES" },
  { label: "WBR", value: "WBR" },
]

const MONTHLY_SCORE_LABELS: Record<string, string> = {
  "1": "Detractor",
  "5": "Neutral",
  "9": "Promoter",
  "100": "Not Scorable",
  "200": "Too Early To Score",
};

export const getMonthlyScoreLabel = (score: unknown) =>
  MONTHLY_SCORE_LABELS[String(score)] || "Not Rated";

export const getMonthlyScoreColor = (label: string) =>
  label === "Not Rated"
    ? "#cec1f2"
    : accountRatingOptions.find((option) => option.label === label)
        ?.labelColour || "#cec1f2";

export const formatMonthLabel = (monthKey: string) => {
  const [year, month] = monthKey.split("-").map(Number);
  const monthDate = new Date(year, month, 0);

  return monthDate.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
  });
};

export const getInteractionCountByMonth = (
  items: TimelineItem[] = []
): Record<string, number> =>
  items.reduce<Record<string, number>>((counts, item) => {
    if (item.type !== "interaction") return counts;

    const itemMonthKey = getMonthKey(new Date(item.start_time));
    counts[itemMonthKey] = (counts[itemMonthKey] || 0) + 1;
    return counts;
  }, {});

  export const formatDateLabel = (timestamp: number) => {
    const d = new Date(timestamp);
    const day = d.toLocaleString("en-GB", { day: "2-digit" });
    const month = d.toLocaleString("en-GB", { month: "short" });
    const year = d.toLocaleString("en-GB", { year: "numeric" });
    return `${day} ${month}, ${year}`;
  };

  export const isSameCalendarDay = (a: number, b: number) => {
    const da = new Date(a);
    const db = new Date(b);
    return (
      da.getFullYear() === db.getFullYear() &&
      da.getMonth() === db.getMonth() &&
      da.getDate() === db.getDate()
    );
  };

  export const formatRelationshipType = (value?: string) => {
    if (!value) return "";

    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  };

  export const displayValue = (val: any) => {
    if (val === null || val === undefined || val === "") return "---";
    return val;
  };

  export const audioExtensions = ["mp3", "m4a"];

  export const validateEmail = (email: string): boolean => {
    if (!email) return false;
    const trimmed = email.trim();
    return trimmed.includes("@") && trimmed.includes(".") && isValidEmail(trimmed);
  };
  
  export const ACTION_OPTIONS = [
    { label: "Accepted", value: "ACCEPTED" },
    { label: "Discarded", value: "DISCARDED" },
    { label: "No Action Taken", value: "NO_ACTION_TAKEN" },
  ];

  export const downloadBlob = (
    data: Blob | string | ArrayBuffer,
    filename: string,
    contentType = "text/csv"
  ) => {
    const blob = data instanceof Blob ? data : new Blob([data], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const readJsonErrorMessage = (text: string, fallback = "Failed to download report.") => {
    try {
      const parsed = JSON.parse(text);
      return parsed?.message || parsed?.error || fallback;
    } catch {
      return fallback;
    }
  };

  export const parseBlobError = async (
    error: any,
    fallback = "Failed to download report."
  ): Promise<string> => {
    const data = error?.response?.data;
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        return readJsonErrorMessage(text, fallback);
      } catch {
        // fall through
      }
    }
    if (typeof data === "string" && data.trim()) {
      return readJsonErrorMessage(data, fallback);
    }
    if (data && typeof data === "object" && !(data instanceof Blob)) {
      return data.message || data.error || error?.message || fallback;
    }
    return error?.message || fallback;
  };

  /** Validates export payload; returns a fresh Blob ready to download (original may be consumed). */
  export const prepareExportDownload = async (data: Blob): Promise<Blob> => {
    const type = (data.type || "").toLowerCase();
    const text = await data.text();
    const trimmed = text.trim();

    const looksLikeJson =
      type.includes("json") ||
      (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
      (trimmed.startsWith("[") && trimmed.endsWith("]"));

    if (looksLikeJson) {
      throw new Error(readJsonErrorMessage(trimmed));
    }

    if (!trimmed) {
      throw new Error("No data available to download.");
    }

    const lines = trimmed.split(/\r?\n/).filter((line) => line.trim().length > 0);
    if (lines.length <= 1) {
      throw new Error("No data available to download.");
    }

    return new Blob([text], {
      type: type.includes("csv") || type.includes("text/") ? data.type || "text/csv" : "text/csv",
    });
  };

  export const notetakerOptions = [
    { label: "Enabled", value: "Enabled" },
    { label: "Disabled", value: "Disabled" },
  ];

  export const options =[
    { label: "Enable", value: "enable" },
    { label: "Disable", value: "disable" },
  ];

  export const botSettingsOptions = [
    { label: "All Meetings", value: "all" },
    { label: "Only External Meetings", value: "external" },
    { label: "Only Internal Meetings", value: "internal" },
    { label: "On Demand", value: "ondemand" },
  ];

  export const allowedListToUpload =[
    "pdf", "doc", "docx", "rtf", "txt", "csv", "xlsx", "jpg", "jpeg", "png", "mp3", "mp4", "m4a", "wav"
  ];

  export const aiSupportedExtensions = ["mp4", "m4a", "mp3"];

  export const isAiStatusInProgress = (status: string | null | undefined): boolean => {
    if (!status) return false;
    const clean = status.toLowerCase().trim().replace(/[\s_-]/g, "");
    return (
      clean === "inprogress" ||
      clean === "transcriptgenerationinprogress"
    );
  };

  export const isAiStatusCompleted = (status: string | null | undefined): boolean => {
    if (!status) return false;
    return status.toLowerCase().trim() === "completed";
  };

  export const isGroupAiInProgress = (
    statuses: (string | null | undefined)[],
    isTriggered: boolean = false
  ): boolean => {
    if (isTriggered) return true;
    return statuses.some((status) => isAiStatusInProgress(status));
  };

  export const isGroupAiCompleted = (
    statuses: (string | null | undefined)[] = [],
    isTriggered: boolean = false
  ): boolean => {
    if (isTriggered) return false;
    if (isGroupAiInProgress(statuses, false)) return false;

    return statuses.some(isAiStatusCompleted);
  };

  export const getFileExtension = (fileName: string) => {
    const cleanFileName = fileName.split("?")[0];
    return cleanFileName.includes(".")
      ? cleanFileName.split(".").pop()?.toLowerCase() || ""
      : "";
  };

  export const getGroupStatusMap = (
    assets: {
      interactionGroupId?: string | null;
      aiAnalysisStatus?: string | null;
      assetSource?: string | null;
    }[]
  ): Record<string, string[]> => {
    return assets.reduce((acc: Record<string, string[]>, asset) => {
      const groupId = asset.interactionGroupId;

      if (!groupId) {
        return acc;
      }

      if (!acc[groupId]) {
        acc[groupId] = [];
      }

      const source = asset.assetSource;
      if (source?.toLowerCase() === "account") {
        acc[groupId].push((asset.aiAnalysisStatus ?? "").toLowerCase());
      }

      return acc;
    }, {});
  };

export const isValidMeetingLink = (url: string) => {
  const googleMeet = /^https:\/\/meet\.google\.com\/[a-z]{3}-[a-z]{4}-[a-z]{3}(\?.*)?$/;
  const zoom = /^https:\/\/([a-zA-Z0-9-]+\.)?zoom\.us\/j\/\d+(\?.*)?$/;
  const microsoftTeams = /^https:\/\/teams\.microsoft\.com\/.+$/;
  const teamsLive = /^https:\/\/teams\.live\.com\/.+$/;

  return googleMeet.test(url) || zoom.test(url) || microsoftTeams.test(url) || teamsLive.test(url);
};

export const getMeetingLinkError = (url: string) => {
  if (!url.trim()) {
    return "Meeting link is required";
  }

  if (!url.startsWith("https://")) {
    return "Meeting link must start with https://";
  }

  if (!isValidMeetingLink(url)) {
    return "Only Google Meet, Zoom, and Microsoft Teams links are allowed";
  }

  return "";
};

export const relationshipTypeFilterOptions = relationshipTypeOptions.filter(
  (option) =>
    ["client", "prospect", "vendor", "partner"].includes(option.value)
);

export const ACCOUNT_THEME_TAGS_CONFIG: AccountThemeTagConfig[] = [
  {
    id: "execEscalation",
    label: "Exec Escalation",
    key: "execEscalationCount",
    themeCategoryKey: "executiveEscalations",
    dotColor: "#FF4D4D",
    backgroundColor: "#FFE4E6",
    borderColor: "#FECACA",
    textColor: "#808080",
    hoverBackgroundColor: "#FFD6D9",
    hoverBorderColor: "#FDA4AF",
    hoverTextColor: "#B42318",
  },

  {
    id: "managerEscalation",
    label: "Manager Escalation",
    key: "managerEscalationCount",
    themeCategoryKey: "managerEscalations",
    dotColor: "#F7B750",
    backgroundColor: "#FFEDD5",
    borderColor: "#FDBA74",
    textColor: "#808080",
    hoverBackgroundColor: "#FFE4C2",
    hoverBorderColor: "#FB923C",
    hoverTextColor: "#9A3412",
  },

  {
    id: "operationalRisk",
    label: "Operational Risk",
    key: "operationalRiskCount",
    themeCategoryKey: "operationalRisks",
    dotColor: "#F7B750",
    backgroundColor: "#FEF3C7",
    borderColor: "#FDE68A",
    textColor: "#808080",
    hoverBackgroundColor: "#FDEBB3",
    hoverBorderColor: "#FACC15",
    hoverTextColor: "#92400E",
  },

  {
    id: "clientControllable",
    label: "Client Controllable",
    key: "clientControllableCount",
    themeCategoryKey: "clientControllable",
    dotColor: "#3B82F6",
    backgroundColor: "#EFF6FF",
    borderColor: "#BFDBFE",
    textColor: "#808080",
    hoverBackgroundColor: "#DCEEFF",
    hoverBorderColor: "#93C5FD",
    hoverTextColor: "#005580",
  },

  {
    id: "positive",
    label: "Positive",
    key: "positiveCount",
    themeCategoryKey: "positive",
    dotColor: "#1EC96B",
    backgroundColor: "#ECFDF5",
    borderColor: "#A7F3D0",
    textColor: "#808080",
    hoverBackgroundColor: "#DDF9E9",
    hoverBorderColor: "#6CE9A6",
    hoverTextColor: "#05603A",
  },
];

export const getFullEmailDomain = (email?: string) => {
  if (!email) return "";
  const trimmed = email.trim().toLowerCase();
  if (!trimmed.includes("@")) return trimmed;
  return trimmed.split("@").pop()?.trim().toLowerCase() || "";
};

export const formatArrAmount = (value: unknown) => {
  const amount = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(amount)) return "---";
  return `$${amount.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
};

export const formatNextMeeting = (value: unknown) => {
  if (typeof value !== "string" || value === "") return "";
  if (!dayjs(value).isValid()) return value;
  return dayjs(value).format("MMM D, h:mma").replace(":00", "");
};

export const toOpportunityFilterOptions = (items: any[]): Options[] => {
  const seen = new Set<string>();
  const options: Options[] = [];
  items.forEach((item) => {
    const value = String(item?.id ?? "").trim();
    const label = String(item?.opportunityName ?? "").trim();
    if (!value || !label || seen.has(value)) return;
    seen.add(value);
    options.push({ label, value });
  });
  return options;
};

export const toRecordFilterOptions = (record: unknown): Options[] => {
  if (!record || typeof record !== "object" || Array.isArray(record)) return [];
  const seen = new Set<string>();
  const options: Options[] = [];
  Object.entries(record as Record<string, unknown>).forEach(([id, name]) => {
    const value = String(id).trim();
    const label = String(name ?? "").trim();
    if (!value || !label || seen.has(value)) return;
    seen.add(value);
    options.push({ label, value });
  });
  return options;
};

export const toFilterOptions = (items: any[]): Options[] => {
  const seen = new Set<string>();
  const options: Options[] = [];
  items.forEach((item) => {
    if (item == null) return;
    if (typeof item === "string") {
      const value = item.trim();
      if (!value || seen.has(value)) return;
      seen.add(value);
      options.push({ label: value, value });
      return;
    }
    const value = String(item.id || item.value || "").trim();
    const label = String(item.label || item.name || value).trim();
    if (!value || !label || seen.has(value)) return;
    seen.add(value);
    options.push({ label, value });
  });
  return options;
};

export type ContactPerson = {
  name: string;
  email?: string | null;
};

export const parseSpeakers = (rawSpeakers: any): ContactPerson[] => {
  if (!rawSpeakers) return [];

  const contacts: ContactPerson[] = [];

  const addContact = (name: unknown, email?: unknown) => {
    if (typeof name === "string" && name.trim()) {
      contacts.push({
        name: name.trim(),
        email: typeof email === "string" && email.trim() ? email.trim() : null,
      });
    }
  };

  const processObject = (item: Record<string, any>) => {
    if ("name" in item && typeof (item as any).name === "string") {
      addContact((item as any).name, (item as any).email);
    } else {
      Object.entries(item).forEach(([key, val]) => {
        addContact(key, val);
      });
    }
  };

  if (Array.isArray(rawSpeakers)) {
    rawSpeakers.forEach((item) => {
      if (!item) return;
      if (typeof item === "string") {
        addContact(item);
      } else if (typeof item === "object") {
        processObject(item);
      }
    });
  } else if (typeof rawSpeakers === "string") {
    addContact(rawSpeakers);
  } else if (typeof rawSpeakers === "object") {
    processObject(rawSpeakers);
  }

  const namesWithEmail = new Set<string>();
  contacts.forEach((c) => {
    if (c.email) {
      namesWithEmail.add(c.name.toLowerCase());
    }
  });

  const seen = new Set<string>();
  return contacts.filter((c) => {
    const nameLower = c.name.toLowerCase();
    if (!c.email && namesWithEmail.has(nameLower)) {
      return false;
    }
    const key = `${nameLower}|${c.email ? c.email.toLowerCase() : ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const formatSectionTitle = (key: string): string => {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w+/g, (word) => {
      const lower = word.toLowerCase();
      if (
        ["or", "and", "of", "in", "to", "for", "on", "with"].includes(lower)
      ) {
        return lower;
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim();
};
export const isPresignedUrlExpired = (url?: string): boolean => {
  if (!url || typeof url !== "string") return false;
  try {
    const parsed = new URL(url);
    const expires = parsed.searchParams.get("Expires");
    if (expires) {
      const expiryTimestamp = parseInt(expires, 10) * 1000;
      if (!isNaN(expiryTimestamp)) {
        return Date.now() >= expiryTimestamp;
      }
    }
    const amzDate = parsed.searchParams.get("X-Amz-Date");
    const amzExpires = parsed.searchParams.get("X-Amz-Expires");
    if (amzDate && amzExpires) {
      const match = amzDate.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
      if (match) {
        const [, y, m, d, h, min, s] = match;
        const startMs = Date.UTC(
          parseInt(y, 10),
          parseInt(m, 10) - 1,
          parseInt(d, 10),
          parseInt(h, 10),
          parseInt(min, 10),
          parseInt(s, 10)
        );
        const durationMs = parseInt(amzExpires, 10) * 1000;
        if (!isNaN(startMs) && !isNaN(durationMs)) {
          return Date.now() >= startMs + durationMs;
        }
      }
    }
  } catch {
    // Ignore invalid URL
  }
  return false;
};
